import { gql } from '@apollo/client';

export const GET_COMMITTEES = gql`
  query GetActiveCommittees {
    committees(isActive: true) {
      id
      name
      description
      threshold
      votingOptions
      isActive
      fundId {
        id
        name
      }
      memberships {
        id
        userId {
          id
          name
          partner {
            image128
          }
        }
        role
      }
      auditLogs {
        authorName
        trackingValues {
          fieldName
          oldValue
          newValue
          createUserName
          createDate
        }
      }
      memberAuditLogs {
        id
        createDate
        authorName
        membershipRecord {
          id
          role
          userId {
            name
          }
          startDate
          endDate
        }
        trackingValues {
          fieldName
          oldValue
          newValue
          createUserName
          createDate
        }
      }
    }
  }
`;

export const SYNC_COMMITTEE_MEMBERS = gql`
  mutation SyncCommitteeMembers(
    $committeeId: ID!
    $members: [CommitteeMemberInput!]!
  ) {
    syncCommitteeMembers(committeeId: $committeeId, members: $members) {
      ok
      addedMemberships {
        id
        userId {
          id
          name
          partner {
            image128
          }
        }
        role
      }
      removedMemberships {
        id
      }
    }
  }
`;

export const REMOVE_COMMITTEE_MEMBER = gql`
  mutation RemoveCommitteeMember($membershipId: ID!) {
    removeCommitteeMember(membershipId: $membershipId) {
      ok
      membership {
        id
        endDate
      }
    }
  }
`;

export const GET_ACTIVE_FUNDS = gql`
  query GetActiveFunds {
    funds(isActive: true) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const UPDATE_COMMITTEE = gql`
  mutation UpdateCommittee($id: ID!, $input: CommitteeInput!) {
    updateCommittee(id: $id, input: $input) {
      ok
      committee {
        id
        name
        description
        threshold
        fundId {
          id
          name
        }
      }
    }
  }
`;

export const CREATE_COMMITTEE = gql`
  mutation CreateCommittee($input: CommitteeInput!) {
    createCommittee(input: $input) {
      ok
      committee {
        id
        name
        description
        threshold
        fundId {
          id
          name
        }
        memberships {
          id
          userId {
            id
            name
          }
          role
        }
      }
    }
  }
`;

export const DELETE_COMMITTEE = gql`
  mutation DeleteCommittee($id: ID!) {
    deleteCommittee(id: $id) {
      ok
    }
  }
`;

export interface AuditLog {
  authorName: string;
  trackingValues: TrackingValue[];
}

export interface CreateCommitteeData {
  createCommittee: {
    committee: {
      description: null | string;
      fundId: {
        id: string;
        name: string;
      } | null;
      id: string;
      name: string;
      threshold: number;
    } | null;
    ok: boolean;
  };
}

export interface DeleteCommitteeData {
  deleteCommittee: {
    ok: boolean;
  };
}

export interface GetActiveFundsData {
  funds: {
    edges: {
      node: {
        id: string;
        name: string;
      };
    }[];
  };
}

export interface GetCommitteesData {
  committees: {
    __typename: 'CommitteeNode';
    allMemberships: {
      __typename: 'CommitteeMembershipNode';
      endDate: null | string;
      id: string;
      role: null | string;
      startDate: null | string;
      userId: {
        __typename: 'UserNode';
        email: string;
        id: string;
        name: string;
        partner: {
          image128: string;
        } | null;
      } | null;
    }[];
    auditLogs: AuditLog[];
    description: null | string;
    fundId: {
      __typename: 'FundNode';
      id: string;
      name: null | string;
    } | null;
    id: string;
    isActive: boolean;
    memberAuditLogs: MemberAuditLog[];
    memberships: {
      __typename: 'CommitteeMembershipNode';
      endDate: null | string;
      id: string;
      role: null | string;
      startDate: null | string;
      userId: {
        __typename: 'UserNode';
        email: string;
        id: string;
        name: string;
        partner: {
          image128: string;
        } | null;
      } | null;
    }[];
    name: null | string;
    stageMapping: null | string;
    threshold: number;
    votingOptions: string;
  }[];
}

export interface MemberAuditLog {
  authorName: string;
  createDate: null | string;
  id: string;
  membershipRecord: {
    endDate: null | string;
    id: string;
    role: null | string;
    startDate: null | string;
    userId: {
      name: string;
    } | null;
  } | null;
  trackingValues: TrackingValue[];
}

export interface TrackingValue {
  createDate: null | string;
  createUserName: null | string;
  fieldName: string;
  newValue: null | string;
  oldValue: null | string;
}
